import React, { useMemo, useRef, useEffect, useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import 'mathlive';

/**
 * Core SVG component that renders the mapping diagram
 * Displays two columns of items with arrow connections between them
 */
const MappingDiagram = ({
  leftItems = [],
  rightItems = [],
  connections = [],
  selectedLeft = null,
  hoveredConnection = null,
  onLeftClick,
  onRightClick,
  onConnectionClick,
  onConnectionHover,
  readOnly = false,
  config = {},
  connectionStatuses = null, // For showing correct/incorrect in review mode
}) => {
  const theme = useTheme();
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 600, height: 400 });

  // Configuration
  const {
    leftLabel = 'X',
    rightLabel = 'Y',
    showLabels = true,
    ovalShape = true,
  } = config;

  // Layout constants
  const padding = 20;
  const ovalPadding = 15;
  const itemHeight = 40;
  const itemWidth = 100;
  const labelHeight = showLabels ? 30 : 0;

  // Calculate dimensions based on items
  const maxItems = Math.max(leftItems.length, rightItems.length, 1);
  const contentHeight = maxItems * itemHeight + ovalPadding * 2 + labelHeight;
  const svgHeight = contentHeight + padding * 2;
  const svgWidth = dimensions.width;

  // Update dimensions on resize
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const { width } = containerRef.current.getBoundingClientRect();
        setDimensions({ width: Math.max(width, 400), height: svgHeight });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, [svgHeight]);

  // Calculate positions
  const layout = useMemo(() => {
    const leftOvalX = padding + itemWidth / 2 + ovalPadding;
    const rightOvalX = svgWidth - padding - itemWidth / 2 - ovalPadding;
    const ovalWidth = itemWidth + ovalPadding * 2;
    const ovalHeight = maxItems * itemHeight + ovalPadding * 2;
    const ovalY = padding + labelHeight;

    // Calculate item positions
    const getItemY = (index) => {
      const startY = ovalY + ovalPadding + itemHeight / 2;
      return startY + index * itemHeight;
    };

    return {
      leftOvalX,
      rightOvalX,
      ovalWidth,
      ovalHeight,
      ovalY,
      getItemY,
    };
  }, [svgWidth, maxItems, labelHeight]);

  // Get item position by ID
  const getItemPosition = (itemId, side) => {
    const items = side === 'left' ? leftItems : rightItems;
    const index = items.findIndex((item) => item.id === itemId);
    if (index === -1) return null;

    const x = side === 'left' ? layout.leftOvalX : layout.rightOvalX;
    const y = layout.getItemY(index);

    return {
      x,
      y,
      // Connection points (edge of item circle)
      connectionX: side === 'left' ? x + itemWidth / 2 + 5 : x - itemWidth / 2 - 5,
      connectionY: y,
    };
  };

  // Colors
  const colors = {
    oval: theme.palette.mode === 'dark' ? '#2d3748' : '#f0f4f8',
    ovalStroke: theme.palette.mode === 'dark' ? '#4a5568' : '#cbd5e0',
    item: theme.palette.mode === 'dark' ? '#4a5568' : '#e2e8f0',
    itemHover: theme.palette.primary.light,
    itemSelected: theme.palette.primary.main,
    itemConnected: theme.palette.success.light,
    text: theme.palette.text.primary,
    textSelected: '#ffffff', // White text for selected items (dark background)
    textConnected: theme.palette.mode === 'dark' ? theme.palette.text.primary : '#1a472a', // Dark green text for connected items
    arrow: theme.palette.warning.main,
    arrowHover: theme.palette.warning.dark,
    correct: theme.palette.success.main,
    incorrect: theme.palette.error.main,
    missing: theme.palette.info.main,
  };

  // Render oval container
  const renderOval = (cx, cy, width, height, label) => (
    <g>
      {ovalShape ? (
        <ellipse
          cx={cx}
          cy={cy + height / 2}
          rx={width / 2}
          ry={height / 2}
          fill={colors.oval}
          stroke={colors.ovalStroke}
          strokeWidth={2}
        />
      ) : (
        <rect
          x={cx - width / 2}
          y={cy}
          width={width}
          height={height}
          rx={10}
          ry={10}
          fill={colors.oval}
          stroke={colors.ovalStroke}
          strokeWidth={2}
        />
      )}
      {showLabels && (
        <text
          x={cx}
          y={cy - 8}
          textAnchor="middle"
          fill={colors.text}
          fontSize={16}
          fontWeight="bold"
        >
          {label}
        </text>
      )}
    </g>
  );

  // Render item
  const renderItem = (item, index, side) => {
    const pos = getItemPosition(item.id, side);
    if (!pos) return null;

    const isSelected = side === 'left' && selectedLeft === item.id;
    const isConnected =
      side === 'left'
        ? connections.some((c) => c.from === item.id)
        : connections.some((c) => c.to === item.id);

    let fillColor = colors.item;
    let textColor = colors.text;
    if (isSelected) {
      fillColor = colors.itemSelected;
      textColor = colors.textSelected;
    } else if (isConnected) {
      fillColor = colors.itemConnected;
      textColor = colors.textConnected;
    }

    const handleClick = () => {
      if (readOnly) return;
      if (side === 'left' && onLeftClick) {
        onLeftClick(item.id);
      } else if (side === 'right' && onRightClick) {
        onRightClick(item.id);
      }
    };

    return (
      <g
        key={item.id}
        onClick={handleClick}
        style={{ cursor: readOnly ? 'default' : 'pointer' }}
      >
        <rect
          x={pos.x - itemWidth / 2}
          y={pos.y - itemHeight / 2 + 5}
          width={itemWidth}
          height={itemHeight - 10}
          rx={5}
          ry={5}
          fill={fillColor}
          stroke={isSelected ? colors.itemSelected : 'transparent'}
          strokeWidth={isSelected ? 2 : 0}
          className={!readOnly ? 'mapping-item' : ''}
        />
        <foreignObject
          x={pos.x - itemWidth / 2 + 5}
          y={pos.y - itemHeight / 2 + 5}
          width={itemWidth - 10}
          height={itemHeight - 10}
        >
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              pointerEvents: 'none',
            }}
          >
            {item.type === 'math' && item.value ? (
              <math-field
                read-only
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '0.85rem',
                  color: textColor,
                  pointerEvents: 'none',
                }}
              >
                {item.value}
              </math-field>
            ) : (
              <Typography
                variant="body2"
                sx={{
                  color: textColor,
                  textAlign: 'center',
                  fontSize: '0.85rem',
                  lineHeight: 1.2,
                  wordBreak: 'break-word',
                }}
              >
                {item.value || `Item ${index + 1}`}
              </Typography>
            )}
          </Box>
        </foreignObject>
      </g>
    );
  };

  // Render connection arrow
  const renderConnection = (connection, index) => {
    const fromPos = getItemPosition(connection.from, 'left');
    const toPos = getItemPosition(connection.to, 'right');

    if (!fromPos || !toPos) return null;

    // Determine color based on status
    let strokeColor = colors.arrow;
    let strokeWidth = 2;
    let strokeDasharray = 'none';

    if (connectionStatuses) {
      const status = connectionStatuses.find(
        (s) => s.from === connection.from && s.to === connection.to
      );
      if (status) {
        if (status.status === 'correct') strokeColor = colors.correct;
        else if (status.status === 'incorrect') strokeColor = colors.incorrect;
        else if (status.status === 'missing') {
          strokeColor = colors.missing;
          strokeDasharray = '5,5';
        }
      }
    }

    const isHovered =
      hoveredConnection &&
      hoveredConnection.from === connection.from &&
      hoveredConnection.to === connection.to;

    if (isHovered) {
      strokeColor = colors.arrowHover;
      strokeWidth = 3;
    }

    // Calculate curve control point
    const midX = (fromPos.connectionX + toPos.connectionX) / 2;

    const pathD = `M ${fromPos.connectionX} ${fromPos.connectionY}
                   C ${midX} ${fromPos.connectionY},
                     ${midX} ${toPos.connectionY},
                     ${toPos.connectionX} ${toPos.connectionY}`;

    const handleClick = (e) => {
      e.stopPropagation();
      if (!readOnly && onConnectionClick) {
        onConnectionClick(connection.from, connection.to);
      }
    };

    const handleMouseEnter = () => {
      if (onConnectionHover) {
        onConnectionHover(connection);
      }
    };

    const handleMouseLeave = () => {
      if (onConnectionHover) {
        onConnectionHover(null);
      }
    };

    return (
      <g key={`${connection.from}-${connection.to}-${index}`}>
        {/* Invisible wider path for easier clicking */}
        {!readOnly && (
          <path
            d={pathD}
            stroke="transparent"
            strokeWidth={15}
            fill="none"
            style={{ cursor: 'pointer' }}
            onClick={handleClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          />
        )}
        {/* Visible arrow path */}
        <path
          d={pathD}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          strokeDasharray={strokeDasharray}
          fill="none"
          markerEnd={`url(#arrowhead-${strokeColor.replace('#', '')})`}
          style={{ pointerEvents: 'none' }}
        />
      </g>
    );
  };

  // Render missing connections (for review mode)
  const renderMissingConnections = () => {
    if (!connectionStatuses) return null;

    return connectionStatuses
      .filter((s) => s.status === 'missing')
      .map((connection, index) => {
        const fromPos = getItemPosition(connection.from, 'left');
        const toPos = getItemPosition(connection.to, 'right');

        if (!fromPos || !toPos) return null;

        const midX = (fromPos.connectionX + toPos.connectionX) / 2;

        const pathD = `M ${fromPos.connectionX} ${fromPos.connectionY}
                       C ${midX} ${fromPos.connectionY},
                         ${midX} ${toPos.connectionY},
                         ${toPos.connectionX} ${toPos.connectionY}`;

        return (
          <path
            key={`missing-${connection.from}-${connection.to}-${index}`}
            d={pathD}
            stroke={colors.missing}
            strokeWidth={2}
            strokeDasharray="5,5"
            fill="none"
            markerEnd={`url(#arrowhead-${colors.missing.replace('#', '')})`}
            opacity={0.7}
          />
        );
      });
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        minHeight: svgHeight,
        position: 'relative',
        '& .mapping-item:hover': {
          filter: readOnly ? 'none' : 'brightness(1.1)',
        },
      }}
    >
      <svg
        width="100%"
        height={svgHeight}
        viewBox={`0 0 ${svgWidth} ${svgHeight}`}
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Arrow marker definitions */}
        <defs>
          <marker
            id={`arrowhead-${colors.arrow.replace('#', '')}`}
            markerWidth="10"
            markerHeight="7"
            refX="9"
            refY="3.5"
            orient="auto"
          >
            <polygon points="0 0, 10 3.5, 0 7" fill={colors.arrow} />
          </marker>
          <marker
            id={`arrowhead-${colors.arrowHover.replace('#', '')}`}
            markerWidth="10"
            markerHeight="7"
            refX="9"
            refY="3.5"
            orient="auto"
          >
            <polygon points="0 0, 10 3.5, 0 7" fill={colors.arrowHover} />
          </marker>
          <marker
            id={`arrowhead-${colors.correct.replace('#', '')}`}
            markerWidth="10"
            markerHeight="7"
            refX="9"
            refY="3.5"
            orient="auto"
          >
            <polygon points="0 0, 10 3.5, 0 7" fill={colors.correct} />
          </marker>
          <marker
            id={`arrowhead-${colors.incorrect.replace('#', '')}`}
            markerWidth="10"
            markerHeight="7"
            refX="9"
            refY="3.5"
            orient="auto"
          >
            <polygon points="0 0, 10 3.5, 0 7" fill={colors.incorrect} />
          </marker>
          <marker
            id={`arrowhead-${colors.missing.replace('#', '')}`}
            markerWidth="10"
            markerHeight="7"
            refX="9"
            refY="3.5"
            orient="auto"
          >
            <polygon points="0 0, 10 3.5, 0 7" fill={colors.missing} />
          </marker>
        </defs>

        {/* Left oval */}
        {renderOval(
          layout.leftOvalX,
          layout.ovalY,
          layout.ovalWidth,
          layout.ovalHeight,
          leftLabel
        )}

        {/* Right oval */}
        {renderOval(
          layout.rightOvalX,
          layout.ovalY,
          layout.ovalWidth,
          layout.ovalHeight,
          rightLabel
        )}

        {/* Connection arrows */}
        {connections.map((conn, i) => renderConnection(conn, i))}

        {/* Missing connections (review mode) */}
        {renderMissingConnections()}

        {/* Left items */}
        {leftItems.map((item, i) => renderItem(item, i, 'left'))}

        {/* Right items */}
        {rightItems.map((item, i) => renderItem(item, i, 'right'))}
      </svg>

      {/* Selection hint */}
      {selectedLeft && !readOnly && (
        <Typography
          variant="caption"
          sx={{
            position: 'absolute',
            bottom: 5,
            left: '50%',
            transform: 'translateX(-50%)',
            color: 'text.secondary',
          }}
        >
          Click an item on the right to connect, or click the selected item to deselect
        </Typography>
      )}
    </Box>
  );
};

export default MappingDiagram;
